/**
 * oathera-passport-governance task 14.1: the BFF client for the AIS admin governance contracts
 * (design §8.2). Every write carries a 1..500 reason, a client-generated opId, and (for a strict
 * route) a two-factor code the BFF verifies and strips. The client never logs a body and surfaces
 * only the fixed, secret-free error codes: `impact_changed`, `owner_not_active`, `owner_held`,
 * `activation_in_progress`, `version_conflict`, `mfa_required`/`mfa_enrollment_required`,
 * `temporarily_unavailable` (503, retryable). No raw principal, reason text, setter, or signed
 * Passport ever reaches this layer.
 */
import { API_BASE } from "../../../lib/api.ts";

export const PG_ROOT = "passport-governance";
export const PG_REASON_MAX = 500;
export const RISK_LEVELS = ["low", "medium", "high", "critical"] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];
export type RiskValue = RiskLevel | "unassessed";
export type OwnerLossPolicy = "allow" | "revoke" | "delegate" | "transfer";

export interface EffectiveRisk {
  readonly value: RiskValue;
  readonly source: "agent" | "tenant_default" | "none";
}
export interface AgentGovernance {
  readonly effectiveRisk: EffectiveRisk;
  readonly risk: { readonly level: RiskLevel; readonly recordVersion: number } | null;
  readonly owner: {
    readonly memberRef: string;
    readonly state: "active" | "lost" | "identity_invalid";
  } | null;
  readonly hold: boolean;
}
export interface PassportSummary {
  readonly available: boolean;
  readonly effectiveRisk: RiskValue;
  readonly lifecycle: string;
}
export interface RosterMember {
  readonly memberRef: string;
  readonly displayName: string;
  readonly email: string;
  readonly role: string;
  readonly status: string;
  readonly mappingReady: boolean;
}

export type GovResult<T> =
  | { readonly ok: true; readonly body: T }
  | { readonly ok: false; readonly status: number; readonly code: string };

/** Narrow a GovResult to its error branch. */
export function govResultIsError<T>(
  r: GovResult<T>,
): r is { readonly ok: false; readonly status: number; readonly code: string } {
  return r.ok === false;
}

/** A short, client-generated op id (idempotency key); never guessable from content. */
export function newOpId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return `op-${[...bytes].map((b) => b.toString(16).padStart(2, "0")).join("")}`;
}

function errorCode(body: unknown, status: number): string {
  if (typeof body === "object" && body !== null) {
    const record = body as Record<string, unknown>;
    if (typeof record["error"] === "string") return record["error"];
    const error = record["error"];
    if (
      typeof error === "object" &&
      error !== null &&
      typeof (error as Record<string, unknown>)["code"] === "string"
    ) {
      return String((error as Record<string, unknown>)["code"]);
    }
  }
  return `http_${status}`;
}

async function request<T>(
  method: string,
  subpath: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<GovResult<T>> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE}/service-admin/ais/${PG_ROOT}/${subpath.replace(/^\/+/, "")}`,
      {
        method,
        credentials: "same-origin",
        headers: { accept: "application/json", "content-type": "application/json" },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        ...(signal === undefined ? {} : { signal }),
      },
    );
  } catch {
    return { ok: false, status: 0, code: "network" };
  }
  const parsed: unknown = await response.json().catch(() => null);
  if (response.ok) return { ok: true, body: parsed as T };
  return { ok: false, status: response.status, code: errorCode(parsed, response.status) };
}

// ---- Reads ------------------------------------------------------------------------------------

export const getAgentGovernance = (
  ref: string,
  signal?: AbortSignal,
): Promise<GovResult<AgentGovernance>> =>
  request("GET", `agents/${encodeURIComponent(ref)}/governance`, undefined, signal);

export const getPassportSummary = (
  ref: string,
  signal?: AbortSignal,
): Promise<GovResult<PassportSummary>> =>
  request("GET", `agents/${encodeURIComponent(ref)}/passport-summary`, undefined, signal);

export const getRoster = (
  cursor: string | null,
  signal?: AbortSignal,
): Promise<GovResult<{ members: readonly RosterMember[] }>> =>
  request(
    "GET",
    `roster${cursor === null ? "" : `?cursor=${encodeURIComponent(cursor)}`}`,
    undefined,
    signal,
  );

/**
 * The read-only activation preflight (design §3.3, §6.5; rollout task 20.1/21.2). Admin-read
 * capability only, flag-off-safe; it mutates nothing. Reports opaque conflict blockers, immediate
 * and deferred manager-reference changes, and affected counts, so an operator can review the
 * activation impact before any flag flip. Returns 200 when nothing blocks, 409 when a blocker exists.
 */
export interface ActivationPreflight {
  readonly tenantId: string;
  readonly ok: boolean;
  readonly riskAffectedCount: number;
  readonly ownerAffectedCount: number;
  readonly activationContentAffectedCount: number;
  readonly projectionUnaffectedCount: number;
  readonly immediateManagerChangeCount: number;
  readonly deferredManagerChangeCount: number;
  readonly managerSwitches: readonly {
    readonly agent: string;
    readonly from: string | null;
    readonly to: string | null;
  }[];
  readonly deferredManagerSwitches: readonly {
    readonly agent: string;
    readonly pair: string | null;
  }[];
  readonly blockers: readonly {
    readonly agent?: string;
    readonly reason: string;
    readonly pairDigest?: string;
  }[];
}

export const getActivationPreflight = (
  signal?: AbortSignal,
): Promise<GovResult<ActivationPreflight>> =>
  request("GET", "activation-preflight", undefined, signal);

/**
 * The tenant governance settings the console renders (design §8.2 read rows): the tenant-default
 * risk record plus its fresh unoverridden-agent count, and the effective owner-loss policy with its
 * delegate display and owned count. The BFF proxies the two admin reads
 * (`GET tenant-settings/passport-risk` and `GET tenant-settings/owner-loss`); this is the single
 * read shape {@link getTenantGovernanceSettings} composes for {@link PassportGovernanceSettings}.
 */
export interface TenantGovernanceState {
  readonly defaultRisk: RiskLevel | null;
  readonly defaultRecordVersion: number;
  readonly unoverriddenAgentCount: number;
  readonly ownerLossPolicy: OwnerLossPolicy;
  readonly policyVersion: number;
  readonly delegateDisplay: string | null;
  readonly ownedCount: number;
}

function asNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

function asRiskLevel(value: unknown): RiskLevel | null {
  return typeof value === "string" && (RISK_LEVELS as readonly string[]).includes(value)
    ? (value as RiskLevel)
    : null;
}

const OWNER_LOSS_POLICIES = ["allow", "revoke", "delegate", "transfer"] as const;

function asOwnerLossPolicy(value: unknown): OwnerLossPolicy {
  // The design row says an absent record reports `revoke`; default to it defensively.
  return typeof value === "string" && (OWNER_LOSS_POLICIES as readonly string[]).includes(value)
    ? (value as OwnerLossPolicy)
    : "revoke";
}

/**
 * Compose the two tenant-settings admin reads into the single {@link TenantGovernanceState} the
 * settings screen consumes. Field extraction is defensive (the auth parser's pattern): a missing or
 * malformed field degrades to a safe default rather than throwing. An error on EITHER read fails the
 * composite closed with that read's status/code so the screen shows the fixed message.
 */
export async function getTenantGovernanceSettings(
  signal?: AbortSignal,
): Promise<GovResult<TenantGovernanceState>> {
  const [risk, ownerLoss] = await Promise.all([
    request<unknown>("GET", "tenant-settings/passport-risk", undefined, signal),
    request<unknown>("GET", "tenant-settings/owner-loss", undefined, signal),
  ]);
  if (!risk.ok) return risk;
  if (!ownerLoss.ok) return ownerLoss;
  const riskBody = asRecord(risk.body);
  const ownerBody = asRecord(ownerLoss.body);
  return {
    ok: true,
    body: {
      defaultRisk: asRiskLevel(riskBody["level"] ?? riskBody["defaultRisk"]),
      defaultRecordVersion: asNumber(riskBody["recordVersion"] ?? riskBody["defaultRecordVersion"]),
      unoverriddenAgentCount: asNumber(
        riskBody["unoverriddenAgentCount"] ??
          riskBody["affectedAgentCount"] ??
          riskBody["unoverridden"],
      ),
      ownerLossPolicy: asOwnerLossPolicy(ownerBody["policy"] ?? ownerBody["ownerLossPolicy"]),
      policyVersion: asNumber(ownerBody["policyVersion"] ?? ownerBody["version"]),
      delegateDisplay:
        typeof ownerBody["delegateDisplay"] === "string"
          ? (ownerBody["delegateDisplay"] as string)
          : null,
      ownedCount: asNumber(ownerBody["ownedCount"]),
    },
  };
}

/**
 * The canonical backfill (rollout task 20.1). Superadmin + governance step-up; flag-off-safe. It
 * stages each agent's canonical principal (the STAGED projection columns only) by resolving against
 * core; the active/legacy pair and every signed Passport stay byte-identical. Re-runnable: a
 * retryable core outage defers those agents (`retryable: true`). Reports per-outcome counts and
 * opaque conflict digests for owner review.
 */
export interface BackfillReport {
  readonly tenantId: string;
  readonly scanned: number;
  readonly ready: number;
  readonly unlinked: number;
  readonly conflicts: readonly {
    readonly agent: string;
    readonly code: string;
    readonly pairDigest: string | null;
  }[];
  readonly deferred: readonly string[];
  readonly retryable: boolean;
}

export const runCanonicalBackfill = (opts: WriteOptions): Promise<GovResult<BackfillReport>> =>
  request("POST", "canonical-backfill", writeBody(opts, {}));

/**
 * The activation prepare (rollout task 21.2). Superadmin + activation-capability + governance
 * step-up; flag-off-safe. It records a `preparing` activation intent for `requestedGeneration` (the
 * next generation) and BLOCKS governance writes for the tenant until the intent commits or cancels;
 * it does NOT seal a generation and changes no production flag/var/secret. The closed server body is
 * `{reason, opId, requestedGeneration, approvalRef, kyaAckDigest, preflightDigest}` — `kyaAckDigest`
 * and `preflightDigest` are each 64 lowercase hex; `approvalRef` is an opaque 1..128-char id. The
 * 200 body is `{opId, next_generation, preflightDigest, blocked_writes:true}`.
 */
export interface PrepareResult {
  readonly opId: string;
  readonly next_generation: number;
  readonly preflightDigest: string;
  readonly blocked_writes: boolean;
}

export const runActivationPrepare = (
  opts: WriteOptions & {
    readonly requestedGeneration: number;
    readonly approvalRef: string;
    readonly kyaAckDigest: string;
    readonly preflightDigest: string;
  },
): Promise<GovResult<PrepareResult>> =>
  request(
    "POST",
    "activation/prepare",
    writeBody(opts, {
      requestedGeneration: opts.requestedGeneration,
      approvalRef: opts.approvalRef,
      kyaAckDigest: opts.kyaAckDigest,
      preflightDigest: opts.preflightDigest,
    }),
  );

/**
 * Cancel a stuck `preparing` activation intent (rollout task 21.2; superadmin). It terminates the
 * intent and unblocks governance writes for the tenant; it seals nothing.
 */
export const cancelActivationPrepare = (opts: WriteOptions): Promise<GovResult<unknown>> =>
  request("POST", "activation/cancel", writeBody(opts, {}));

// ---- Writes (each carries reason + opId, and mfaCode for the governance step-up) ---------------

export interface WriteOptions {
  readonly reason: string;
  readonly mfaCode?: string;
}

const writeBody = (
  opts: WriteOptions,
  extra: Record<string, unknown>,
): Record<string, unknown> => ({
  reason: opts.reason,
  opId: newOpId(),
  ...(opts.mfaCode === undefined ? {} : { mfa_code: opts.mfaCode }),
  ...extra,
});

export const setAgentRisk = (
  ref: string,
  level: RiskLevel,
  opts: WriteOptions,
): Promise<GovResult<unknown>> =>
  request("PUT", `agents/${encodeURIComponent(ref)}/risk`, writeBody(opts, { level }));

export const clearAgentRisk = (ref: string, opts: WriteOptions): Promise<GovResult<unknown>> =>
  request("DELETE", `agents/${encodeURIComponent(ref)}/risk`, writeBody(opts, {}));

export const setTenantDefaultRisk = (
  level: RiskLevel,
  confirmedCount: number,
  opts: WriteOptions,
): Promise<GovResult<unknown>> =>
  request("PUT", "tenant-settings/passport-risk", writeBody(opts, { level, confirmedCount }));

export const clearTenantDefaultRisk = (
  confirmedCount: number,
  opts: WriteOptions,
): Promise<GovResult<unknown>> =>
  request("DELETE", "tenant-settings/passport-risk", writeBody(opts, { confirmedCount }));

export const clearOwner = (
  ref: string,
  expectedOwnerVersion: number,
  opts: WriteOptions,
): Promise<GovResult<unknown>> =>
  request(
    "DELETE",
    `agents/${encodeURIComponent(ref)}/owner`,
    writeBody(opts, { expectedOwnerVersion }),
  );

export const setOwnerLossPolicy = (
  policy: OwnerLossPolicy,
  expectedPolicyVersion: number,
  opts: WriteOptions,
  delegate?: { readonly memberRef: string; readonly leaseId: string },
): Promise<GovResult<unknown>> =>
  request(
    "PUT",
    "tenant-settings/owner-loss",
    writeBody(opts, {
      policy,
      expectedPolicyVersion,
      ...(delegate === undefined ? {} : { delegate }),
    }),
  );

/** The human-readable description of each owner-loss policy (shown in the console). */
export const OWNER_LOSS_POLICY_DESCRIPTIONS: Readonly<Record<OwnerLossPolicy, string>> = {
  allow: "Keep the agent active and clear the owner (no accountable owner until reassigned).",
  revoke: "Permanently revoke the agent when its owner is lost.",
  delegate:
    "Reassign the agent to a chosen delegate; fall back to a hold if the delegate is unavailable.",
  transfer:
    "Hold the agent (\u201cAwaiting owner\u201d) until an administrator assigns a new owner.",
};

/** A fixed, secret-free message for each error code surfaced to the operator. */
export function govErrorMessage(code: string): string {
  switch (code) {
    case "impact_changed":
      return "The affected agent count changed; review the new count and confirm again.";
    case "owner_not_active":
      return "That roster member is not an active, mapping-ready owner.";
    case "owner_held":
      return "This agent's owner is held; assign, revoke or delete it to clear the hold.";
    case "manager_principal_unlinked":
      return "This agent's manager is not linked to a canonical principal yet.";
    case "canonical_principal_conflict":
      return "This identity conflicts with an existing canonical principal; resolve it first.";
    case "activation_in_progress":
      return "A governance activation is in progress; try again when it completes.";
    case "version_conflict":
      return "This record changed since it was read; reload and try again.";
    case "mfa_required":
      return "Enter a current two-factor code to continue.";
    case "mfa_enrollment_required":
      return "Set up two-factor authentication before this action.";
    case "temporarily_unavailable":
      return "The service is temporarily unavailable; retry shortly.";
    default:
      return "The request could not be completed.";
  }
}
